import { Badge } from "@/components/ui/badge"
import { Dot, Globe, User } from "lucide-react"
import type { ComponentNavRecord } from "./collectionMenuGrouping"
import { isConnectorBackedCollection } from "./collectionMenuGrouping"
import { CollectionMenuIcon } from "./CollectionMenuIcon"
import { getMongoCollectionNameForFolder, resolveCollectionIdentifier } from "../utils/collectionUtils"
import { getCollectionCreatorBadgeCopy } from "../utils/collectionCreatorBadge"
import { menuNavIconSlotClass } from "./menuNavPrimitives"
import {
  collectionRowShellClasses,
  collectionRowPaddingComfortable,
  collectionRowLabelClasses,
  collectionMenuPurpleIconClasses,
  collectionMenuTrailingIconSlotClass,
} from "./collectionNavRowClassNames"

type CollectionMenuRowProps = {
  component: ComponentNavRecord
  active: boolean
  keyboardSelected: boolean
  listNoView: boolean
  effectiveDebugMode: boolean
  compact: boolean
  mobile: boolean
  menuIndex: number
  registerMenuButton?: (index: number) => (el: HTMLButtonElement | null) => void
  onNavigate: (component: ComponentNavRecord) => void
}

export function CollectionMenuRow({
  component,
  active,
  keyboardSelected,
  listNoView,
  effectiveDebugMode,
  compact,
  mobile,
  menuIndex,
  registerMenuButton,
  onNavigate,
}: CollectionMenuRowProps) {
  const remote = isConnectorBackedCollection(component)
  const creatorBadge = getCollectionCreatorBadgeCopy(component)
  const mongoDebugCollectionLabel =
    component.dataSource === "mongodb"
      ? getMongoCollectionNameForFolder(component as Record<string, unknown>, component.slug) ??
      resolveCollectionIdentifier(component as Record<string, unknown>, component.slug) ??
      (component.collection || component.slug)
      : ""

  return (
    <button
      key={component.uid}
      type="button"
      ref={compact && !mobile ? registerMenuButton?.(menuIndex) : undefined}
      onClick={() => onNavigate(component)}
      title={listNoView ? "You do not have permission to view this table" : undefined}
      className={`${collectionRowShellClasses({
        routeActive: active,
        keyboardSelected,
        listNoView,
      })} ${collectionRowPaddingComfortable}`}
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className={menuNavIconSlotClass}>
          <CollectionMenuIcon
            menuIcon={component.menuIcon}
            className={`w-4 h-4 shrink-0 ${collectionMenuPurpleIconClasses}`}
          />
        </span>
        <span className={`${collectionRowLabelClasses(active)} min-w-0`}>
          <span className="truncate block">{component.name}</span>
        </span>
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {creatorBadge ? (
          creatorBadge.isLabwickAdmin ? (
            <span
              className={collectionMenuTrailingIconSlotClass}
              title="By labwick admin"
              aria-label="By labwick admin"
            >
              <User className={`h-3.5 w-3.5 ${collectionMenuPurpleIconClasses}`} />
            </span>
          ) : creatorBadge.isUnknownUser ? (
            <span
              className={collectionMenuTrailingIconSlotClass}
              title={creatorBadge.title}
              aria-label={creatorBadge.title}
            >
              <Dot
                className={`h-4 w-4 fill-violet-600 stroke-none dark:fill-violet-400 ${collectionMenuPurpleIconClasses}`}
              />
            </span>
          ) : (
            <Badge
              variant="secondary"
              className="text-[0.65rem] font-normal max-w-[9rem] truncate border-violet-200/80 text-violet-950 bg-violet-50 dark:border-violet-700 dark:text-violet-100 dark:bg-violet-950/50"
              title={creatorBadge.title}
            >
              {creatorBadge.text}
            </Badge>
          )
        ) : null}
        {effectiveDebugMode ? (
          <Badge
            variant="outline"
            title={
              component.dataSource === "mongodb"
                ? mongoDebugCollectionLabel || "MongoDB collection"
                : String(component.dataSource ?? "")
            }
            className={`text-[0.65rem] max-w-[11rem] truncate font-mono ${component.dataSource === "mongodb"
              ? "border-green-200 text-green-700 bg-green-50 dark:border-green-700 dark:text-green-300"
              : "border-blue-200 text-blue-700 bg-blue-50 dark:border-blue-700 dark:text-blue-300"
              }`}
          >
            {component.dataSource === "mongodb"
              ? mongoDebugCollectionLabel || "Mongo"
              : String(component.dataSource ?? "—")}
          </Badge>
        ) : null}
        {remote ? (
          <span
            className={collectionMenuTrailingIconSlotClass}
            title="Remote collection"
            aria-label="Remote collection"
          >
            <Globe className={`w-3.5 h-3.5 ${collectionMenuPurpleIconClasses}`} />
          </span>
        ) : null}
      </div>
    </button>
  )
}
